import React, { useCallback, useEffect, useState } from 'react';
import Box from '@components/Atoms/Box';
import {
  CircularProgress,
  InputAdornment,
  Pagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { QuestionGenerationServiceEditorWelcome } from '@components/QuestionGenerationService/styles';
import ShowIf from '@components/Atoms/ShowIf';
import ShadowScrollbar from '@components/Scrollbar/ShadowScrollbars';
import Button from '@components/Atoms/Button';
import Typography from '@components/Atoms/Typography';
import AddIcon from '@mui/icons-material/Add';
import {
  useInstantSearch,
  useSearchBox,
  InstantSearch,
  Configure,
} from 'react-instantsearch';
import useDebounce from '@utils/hooks/useDebounce';
import FleschReadabilityWithCounts from '@components/Selectors/FleschReadability/FleschReadabilityWithCounts';
import FleschKincaidWithCounts from '@components/Selectors/KincaidGrade/KincaidGradeWithCounts';
import { useUser } from '@reducers/user/hooks';
import { useCourseById } from '@reducers/courses/hooks';
import { useParams } from 'react-router-dom';
import PassageListItem from './PassageListItem';
import { MODES } from './Passages';
import { AlgoliaPassage } from './types';
import client from '../../apis/algolia';

export const PassageSource = {
  MY_PASSAGES: 'my-passages',
  CHALKTALK_PASSAGES: 'chalktalk-passages',
  DISTRICT_PASSAGES: 'district-passages',
};

interface PassageListElementProps {
  switchMode: (toMode: string, passage?: AlgoliaPassage | string | null) => void;
  inDialog?: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  searchQuery: string;
  setSearchQuery: (phrase: string) => void;
  readability: string[];
  setReadability: (readability: string[]) => void;
  fleschgrade: string[];
  setFleschgrade: (grade: string[]) => void;
  passageSource: string;
  setPassageSource: (phrase: string) => void;
}

const PassageListElement: React.FC<PassageListElementProps> = ({
  switchMode,
  inDialog,
  currentPage,
  setCurrentPage,
  searchQuery,
  setSearchQuery,
  readability,
  setReadability,
  fleschgrade,
  setFleschgrade,
  passageSource,
  setPassageSource,
}) => {
  const user = useUser();
  const { courseId } = useParams();
  const courseDetails = useCourseById(courseId);
  const districtId = courseDetails?.organization?.district?.id;
  const PAGE_SIZE = 10;
  const [pageCount, setPageCount] = useState(0);

  const { refine } = useSearchBox();
  const { status, results, refresh } = useInstantSearch();
  const { run, isWaiting } = useDebounce(refine, 300);

  // making sure we fetch the data when the component mounts
  useEffect(() => {
    refresh();
  }, [refresh, passageSource]);

  // update the page count and current page when the results change
  useEffect(() => {
    if (!results || results.nbPages === 0) {
      return;
    }

    setPageCount(results.nbPages);
    if (currentPage > results.nbPages) {
      setCurrentPage(results.nbPages);
    } else {
      setCurrentPage(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, setCurrentPage]);

  // handle passage source change
  const handlePassageBankChange = (_: React.MouseEvent<HTMLElement>, value: string) => {
    if (value) {
      setPassageSource(value);
    }
  };

  // handle search query change
  const handleSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    run(event.target.value);
  };

  useEffect(() => {
    if (searchQuery.length > 0) {
      run(searchQuery);
    }
  }, [run, searchQuery]);

  const getFilters = useCallback(() => {
    const filters: string[] = [];
    if (passageSource === PassageSource.CHALKTALK_PASSAGES) {
      filters.push(`Author: Chalktalk`);
    } else if (passageSource === PassageSource.DISTRICT_PASSAGES) {
      filters.push(`'District ID': ${districtId}`);
    } else {
      filters.push(`'Author ID': ${user?.id}`);
    }

    if (readability.length > 0) {
      filters.push(`metadata.lexile: '${readability[0]}'`);
    }

    if (fleschgrade.length > 0) {
      filters.push(`metadata.flesch_kincaid: ${fleschgrade[0]}`);
    }

    return filters.join(' AND ');
  }, [districtId, fleschgrade, passageSource, readability, user?.id]);

  const isLoading = ['loading', 'stalled'].includes(status);

  const isFilterActive =
    searchQuery.length > 0 || readability.length > 0 || fleschgrade.length > 0;

  return (
    <>
      <Configure hitsPerPage={PAGE_SIZE} filters={getFilters()} page={currentPage} />
      <Box
        sx={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: '0.5rem',
        }}
      >
        {/* TOGGLE PASSAGE SOURCE */}
        <ToggleButtonGroup
          color="primary"
          value={passageSource}
          exclusive
          onChange={handlePassageBankChange}
          aria-label="Platform"
          sx={{ minWidth: 'fit-content', flex: 0 }}
          size="small"
        >
          <ToggleButton value={PassageSource.CHALKTALK_PASSAGES}>
            Chalktalk Passages
          </ToggleButton>
          <ToggleButton value={PassageSource.DISTRICT_PASSAGES}>
            District Passages
          </ToggleButton>
          <ToggleButton value={PassageSource.MY_PASSAGES}>My Passages</ToggleButton>
        </ToggleButtonGroup>

        {/* Buttons Container */}
        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
          {/* CREATE PASSAGE BUTTON */}
          <Button
            variant="contained"
            sx={{ alignItems: 'center' }}
            onClick={() => {
              switchMode(MODES.PASSAGE_MODE);
            }}
          >
            <Typography variant="caption">Create Passage</Typography>
            <AddIcon sx={{ fontSize: '1rem' }} />
          </Button>

          {/* GENERATE PASSAGE WITH AI BUTTON */}
          <Button
            variant="contained"
            sx={{ alignItems: 'center' }}
            onClick={() => switchMode(MODES.PASSAGE_GENERATE_MODE)}
          >
            <Typography variant="caption">Generate with AI</Typography>
            <AddIcon sx={{ fontSize: '1rem' }} />
          </Button>
        </Box>
      </Box>

      {/* SEARCH BAR */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <TextField
          id="outlined-basic"
          label="Search Passages"
          variant="outlined"
          size="small"
          value={searchQuery}
          sx={{ width: '20rem' }}
          onChange={handleSearchQueryChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <ShowIf If={isWaiting}>
                  <CircularProgress size="0.875rem" aria-label="Loading" />
                </ShowIf>
                <ShowIf If={!isWaiting}>
                  <SearchIcon />
                </ShowIf>
              </InputAdornment>
            ),
          }}
          data-testid="passage-search-input"
        />
        <FleschReadabilityWithCounts
          width="11rem"
          value={readability}
          onChange={(selected: string[]) => setReadability(selected)}
          topLabel
          enableCounts
        />
        <FleschKincaidWithCounts
          width="11rem"
          value={fleschgrade}
          onChange={(selected) => setFleschgrade(selected)}
          topLabel
          enableCounts
        />
      </Box>

      {/* Loading Indicator */}
      <ShowIf If={isLoading}>
        <Box
          role="status"
          aria-live="polite"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: '12rem',
          }}
        >
          <CircularProgress size={170} aria-hidden="true" />
          <Typography sx={{ position: 'absolute' }}>Fetching Passages...</Typography>
        </Box>
      </ShowIf>

      {/* No Passages Message */}
      <ShowIf If={!isLoading && !isFilterActive && (results?.hits?.length ?? 0) === 0}>
        <QuestionGenerationServiceEditorWelcome>
          <img src="/images/no-content.png" alt="Edit Content" />
          <Typography variant="subtitle2" sx={{ textAlign: 'center', mt: '0rem' }}>
            You have no passages yet.
            <br />
            Click on the &apos;Create Passage&apos; button to add a new passage.
          </Typography>
        </QuestionGenerationServiceEditorWelcome>
      </ShowIf>

      {/* No Passages Match Search Criteria */}
      <ShowIf If={!isLoading && isFilterActive && (results?.hits?.length ?? 0) === 0}>
        <QuestionGenerationServiceEditorWelcome>
          <img src="/images/no-content.png" alt="Edit Content" />
          <Typography variant="subtitle2" sx={{ textAlign: 'center', mt: '0rem' }}>
            No passages match your search criteria.
          </Typography>
        </QuestionGenerationServiceEditorWelcome>
      </ShowIf>

      {/* Passages List */}
      <ShowIf If={!isLoading && (results?.hits?.length ?? 0) > 0}>
        <ShadowScrollbar
          style={{
            height: inDialog ? 'calc(65vh)' : 'calc(56vh)',
            width: '100%',
          }}
        >
          {results?.hits?.map((passage) => (
            <PassageListItem
              key={passage.objectID}
              passage={passage}
              switchMode={switchMode}
              inDialog={inDialog}
            />
          ))}
        </ShadowScrollbar>
        <Pagination
          size="small"
          onChange={(_: React.ChangeEvent<unknown> | null, value: number) => {
            setCurrentPage(value - 1);
          }}
          page={currentPage + 1}
          count={pageCount}
          sx={{
            flex: 0,
            display: 'flex',
            justifyContent: 'center',
            mt: '1rem',
            mb: '3rem',
          }}
        />
      </ShowIf>
    </>
  );
};

const PassageList: React.FC<PassageListElementProps> = ({ ...props }) => (
  <InstantSearch searchClient={client} indexName="learnosity_passages">
    <PassageListElement {...props} />
  </InstantSearch>
);

export default PassageList;
